import useStore from '@components/MainStage/store';
import { NodesTypes } from '@constants/canvas/general';
import {
  CiaSubLayers,
  ConceptualSubLayerTypes,
  ContextualSubLayerTypes,
  InitiativesSubLayerTypes,
  LogicalSubLayerTypes,
  SubLayersWithoutValidStatus,
  SubLayerTypes,
} from '@constants/canvas/layers';
import { useProject } from '@context/Project/ProjectProvider';
import { useProjectFilters } from '@context/ProjectFilters/ProjectFiltersProvider';
import { NodeDTOTypes } from '@store/services/nodes/types';
import { useQuestionnaireResult } from '@store/zustand/questionnaire-result';
import { isCriteriaFieldsMatch } from '@utils/canvasHelpers';
import {
  checkScopeFilter,
  SCOPE_FILTER_NO_OPERATIONALISED,
} from '@utils/domains';
import { traverseNodes } from '@views/Insights/Stage/helpers';
import { Tabs } from '@views/Insights/types';
import { useMemo } from 'react';

const useFilteredCanvas = () => {
  const { edges, nodes, phantoms } = useStore();
  const { insightsTab } = useProject();
  const { result: questionnaireResult } = useQuestionnaireResult();

  const {
    toolbox: { domains },
  } = useProject();

  const {
    reviewStatus,
    validationStatus,
    ciaFilters,
    operationalStatuses,
    operatingModelStatuses,
    partners,
  } = useProjectFilters();

  return useMemo(() => {
    const filteredCanvas = { nodes, edges };
    const connectedBANodes = new Set<string>();

    const isTabFocusedOnOrModelEvolution = [
      Tabs.FocusOn,
      Tabs.ModelEvolution,
    ].includes(insightsTab);

    filteredCanvas.nodes = filteredCanvas.nodes.map((node) => ({
      ...node,
      data: {
        ...node.data,
        canvas: {
          ...node.data.canvas,
          hiddenByFilters: false,
        },
      },
    }));

    if (!isTabFocusedOnOrModelEvolution) {
      filteredCanvas.nodes = filteredCanvas.nodes.map((node) => ({
        ...node,
        hidden: node.data.canvas.collapsed ?? false,
      }));
    }

    // Domain Scope Filter
    if (domains.length) {
      filteredCanvas.nodes = filteredCanvas.nodes.map((node) => {
        const isCollapsed = node.data?.canvas?.collapsed === true;
        if (isCollapsed) return node;

        const isCustomNode = node.type === NodesTypes.CustomNode;

        const isScopeIncluded = checkScopeFilter(
          domains,
          node.data?.dto?.type,
          node.data?.dto?.domainNodes,
        );

        const isDifferentDomain = isCustomNode && !isScopeIncluded;

        const shouldHideNode =
          (node.hidden && isTabFocusedOnOrModelEvolution) || isDifferentDomain;

        return {
          ...node,
          hidden: shouldHideNode,
          data: {
            ...node.data,
            canvas: {
              ...node.data.canvas,
              hiddenByFilters: isDifferentDomain,
            },
          },
        };
      });
    }

    if (domains.includes(SCOPE_FILTER_NO_OPERATIONALISED)) {
      const noOperationalisedAncestorIds = new Set<string>();

      const visited = new Set<string>();

      const ANCESTOR_TYPES = new Set([
        NodeDTOTypes.SecurityAttribute,
        NodeDTOTypes.BusinessAttribute,
        NodeDTOTypes.StrategicEnabler,
        NodeDTOTypes.StrategicPriority,
      ]);

      const traverseParents = (nodeId: string) => {
        if (visited.has(nodeId)) return;
        visited.add(nodeId);
        const found = nodes.find((n) => n.id === nodeId);
        found?.data?.dto?.parentsIds?.forEach((parentId: string) => {
          const parentNode = nodes.find((n) => n.id === parentId);
          if (!parentNode || !ANCESTOR_TYPES.has(parentNode.data?.dto?.type))
            return;
          noOperationalisedAncestorIds.add(parentId);
          traverseParents(parentId);
        });
      };

      filteredCanvas.nodes.forEach((node) => {
        if (
          node.type !== NodesTypes.CustomNode ||
          node.hidden ||
          !checkScopeFilter(
            [SCOPE_FILTER_NO_OPERATIONALISED],
            node.data?.dto?.type,
            node.data?.dto?.domainNodes,
          )
        )
          return;

        traverseParents(node.id);
      });

      if (noOperationalisedAncestorIds.size > 0) {
        filteredCanvas.nodes = filteredCanvas.nodes.map((node) => {
          if (
            node.type !== NodesTypes.CustomNode ||
            !noOperationalisedAncestorIds.has(node.id)
          )
            return node;

          const isAncestorLayer = [
            ContextualSubLayerTypes.StrategicEnablers,
            ContextualSubLayerTypes.StrategicPriorities,
            ConceptualSubLayerTypes.BusinessAttributes,
            ConceptualSubLayerTypes.SecurityAttributes,
          ].includes(node.parentNode as ContextualSubLayerTypes);

          if (!isAncestorLayer) return node;

          return {
            ...node,
            hidden: false,
            data: {
              ...node.data,
              canvas: {
                ...node.data.canvas,
                hiddenByFilters: false,
              },
            },
          };
        });
      }
    }

    // Review Required Filter
    if (reviewStatus.length) {
      filteredCanvas.nodes = filteredCanvas.nodes.map((node) => ({
        ...node,
        hidden:
          node.hidden ||
          !(
            node.type !== NodesTypes.CustomNode ||
            node.data?.dto?.review_required
          ),
        data: {
          ...node.data,
          canvas: {
            ...node.data.canvas,
            hiddenByFilters: !(
              node.type !== NodesTypes.CustomNode ||
              node.data?.dto?.review_required
            ),
          },
        },
      }));
    }

    // Validation Status Filter
    if (validationStatus.length) {
      filteredCanvas.nodes = filteredCanvas.nodes.map((node) => {
        const nodeWithoutValidStatus = SubLayersWithoutValidStatus.includes(
          node.parentNode as SubLayerTypes,
        );

        const isVisible =
          node.type !== NodesTypes.CustomNode ||
          validationStatus.includes(node.data?.dto?.valid_status);

        return {
          ...node,
          hidden: node.hidden || nodeWithoutValidStatus || !isVisible,
          data: {
            ...node.data,
            canvas: {
              ...node.data.canvas,
              hiddenByFilters: nodeWithoutValidStatus || !isVisible,
            },
          },
        };
      });
    }

    // Cia criteria filter
    if (
      Object.values(ciaFilters).some((fields) =>
        Object.values(fields).some((answers) => answers?.length),
      )
    ) {
      const checkNodeCiaMatch = (node: (typeof filteredCanvas.nodes)[0]) => {
        const isAssuranceMatch = isCriteriaFieldsMatch(
          ciaFilters.assurance_level,
          node.data.dto.cia?.assurance_level?.criterias,
        );

        const isCoverageMatch = isCriteriaFieldsMatch(
          ciaFilters.coverage,
          node.data.dto.cia?.coverage?.criterias,
          { passIfNodeFieldEmpty: Boolean(questionnaireResult) },
        );

        const isApplicabilityMatch = isCriteriaFieldsMatch(
          ciaFilters.applicability,
          node.data.dto.cia?.applicability?.criterias,
          { passIfNodeFieldEmpty: Boolean(questionnaireResult) },
        );

        const isComplete = !questionnaireResult || node.data.dto.is_complete;

        return (
          isAssuranceMatch &&
          isCoverageMatch &&
          isApplicabilityMatch &&
          isComplete
        );
      };

      const hideNode = (node: (typeof filteredCanvas.nodes)[0]) => ({
        ...node,
        hidden: true,
        data: {
          ...node.data,
          canvas: { ...node.data.canvas, hiddenByFilters: true },
        },
      });

      const showNode = (node: (typeof filteredCanvas.nodes)[0]) => ({
        ...node,
        hidden: false,
        data: {
          ...node.data,
          canvas: { ...node.data.canvas, hiddenByFilters: false },
        },
      });

      const applyVisibility = (
        node: (typeof filteredCanvas.nodes)[0],
        isVisible: boolean,
      ) => (isVisible ? showNode(node) : hideNode(node));

      if (!questionnaireResult) {
        // Non-questionnaire mode: filter all CIA sublayer nodes directly
        filteredCanvas.nodes = filteredCanvas.nodes.map((node) => {
          if (node.type !== NodesTypes.CustomNode) return node;
          const isCiaSublayer = CiaSubLayers.includes(node.parentNode as any);
          if (!isCiaSublayer) return node;
          return applyVisibility(node, checkNodeCiaMatch(node));
        });
      } else {
        // Questionnaire mode: bottom-up propagation
        // Pass 1: filter functions by CIA match
        filteredCanvas.nodes = filteredCanvas.nodes.map((node) => {
          if (node.type !== NodesTypes.CustomNode) return node;
          if (node.data?.dto?.type === NodeDTOTypes.Technologies)
            return hideNode(node);
          if (node.data?.dto?.type !== NodeDTOTypes.Functions) return node;
          return applyVisibility(node, !node.hidden && checkNodeCiaMatch(node));
        });

        // Pass 2: capabilities — visible if CIA match OR has a visible function child
        const visibleFunctionIds = new Set(
          filteredCanvas.nodes
            .filter(
              (n) =>
                !n.hidden &&
                n.type === NodesTypes.CustomNode &&
                n.data?.dto?.type === NodeDTOTypes.Functions,
            )
            .map((n) => n.id),
        );

        filteredCanvas.nodes = filteredCanvas.nodes.map((node) => {
          if (
            node.type !== NodesTypes.CustomNode ||
            node.data?.dto?.type !== NodeDTOTypes.Capabilities
          )
            return node;
          const childrenIds: string[] = node.data?.dto?.childrenIds ?? [];
          const isVisible =
            (!node.hidden && checkNodeCiaMatch(node)) ||
            childrenIds.some((id) => visibleFunctionIds.has(id));
          return applyVisibility(node, isVisible);
        });

        // Pass 3: services — visible if CIA match OR has a visible capability child
        const visibleCapabilityIds = new Set(
          filteredCanvas.nodes
            .filter(
              (n) =>
                !n.hidden &&
                n.type === NodesTypes.CustomNode &&
                n.data?.dto?.type === NodeDTOTypes.Capabilities,
            )
            .map((n) => n.id),
        );

        filteredCanvas.nodes = filteredCanvas.nodes.map((node) => {
          if (
            node.type !== NodesTypes.CustomNode ||
            node.data?.dto?.type !== NodeDTOTypes.Service
          )
            return node;
          const childrenIds: string[] = node.data?.dto?.childrenIds ?? [];
          const isVisible =
            (!node.hidden && checkNodeCiaMatch(node)) ||
            childrenIds.some((id) => visibleCapabilityIds.has(id));
          return applyVisibility(node, isVisible);
        });

        // Pass 4: hide orphaned capabilities/functions (no visible parent)
        const visibleIds = new Set(
          filteredCanvas.nodes.filter((n) => !n.hidden).map((n) => n.id),
        );
        filteredCanvas.nodes = filteredCanvas.nodes.map((node) => {
          if (node.hidden || node.type !== NodesTypes.CustomNode) return node;
          const nodeType = node.data?.dto?.type;
          if (
            nodeType !== NodeDTOTypes.Capabilities &&
            nodeType !== NodeDTOTypes.Functions
          )
            return node;
          const parentsIds: string[] = node.data?.dto?.parentsIds ?? [];
          const hasVisibleParent = parentsIds.some((id) => visibleIds.has(id));
          if (hasVisibleParent) return node;
          return hideNode(node);
        });
      }
    }

    // Operational Status filter
    const visibleIdsBeforeOpStatus = new Set(
      filteredCanvas.nodes.filter((n) => !n.hidden).map((n) => n.id),
    );

    if (operationalStatuses.length) {
      filteredCanvas.nodes = filteredCanvas.nodes.map((node) => {
        if (node.type !== NodesTypes.CustomNode) return node;

        const isStatusMatch = operationalStatuses.includes(
          node.data.dto?.meta_data?.operational_status,
        );

        const isServiceSublayer =
          node.parentNode === LogicalSubLayerTypes.Services;

        if (isServiceSublayer && !node.hidden && isStatusMatch) {
          traverseNodes(nodes, connectedBANodes, node.id, false, true, true);
          connectedBANodes.delete(node.id);
        }

        if (questionnaireResult) {
          if (node.parentNode === InitiativesSubLayerTypes.ChangeInitiatives) {
            const isComplete = node.data.dto.is_complete;
            const parentsIds: string[] = node.data?.dto?.parentsIds ?? [];
            const hasVisibleParent = parentsIds.some((id) =>
              visibleIdsBeforeOpStatus.has(id),
            );
            const isVisible = isComplete && hasVisibleParent;
            return {
              ...node,
              hidden: node.hidden || !isVisible,
              data: {
                ...node.data,
                canvas: {
                  ...node.data.canvas,
                  hiddenByFilters: !isVisible,
                },
              },
            };
          }

          if (CiaSubLayers.includes(node.parentNode as any)) {
            const opStatus = node.data.dto?.meta_data?.operational_status;
            if (!opStatus || isStatusMatch) return node;
            return {
              ...node,
              hidden: true,
              data: {
                ...node.data,
                canvas: { ...node.data.canvas, hiddenByFilters: true },
              },
            };
          }
        }

        return {
          ...node,
          hidden: node.hidden || !isStatusMatch,
          data: {
            ...node.data,
            canvas: {
              ...node.data.canvas,
              hiddenByFilters: !isStatusMatch,
            },
          },
        };
      });
    }

    // Operating Model filter
    if (operatingModelStatuses.length) {
      filteredCanvas.nodes = filteredCanvas.nodes.map((node) => {
        if (node.type !== NodesTypes.CustomNode) return node;

        const isStatusMatch = operatingModelStatuses.includes(
          node.data.dto?.meta_data?.operational_model,
        );

        return {
          ...node,
          hidden: node.hidden || !isStatusMatch,
          data: {
            ...node.data,
            canvas: {
              ...node.data.canvas,
              hiddenByFilters: !isStatusMatch,
            },
          },
        };
      });
    }

    // Partners filter
    if (partners.length) {
      filteredCanvas.nodes = filteredCanvas.nodes.map((node) => {
        if (node.type !== NodesTypes.CustomNode) return node;

        const isPartnerMatch = partners.some(
          (partner) => node.data.dto?.partners?.includes(partner),
        );

        return {
          ...node,
          hidden: node.hidden || !isPartnerMatch,
          data: {
            ...node.data,
            canvas: {
              ...node.data.canvas,
              hiddenByFilters: !isPartnerMatch,
            },
          },
        };
      });
    }

    // Reveal connected BA nodes if Service is visible
    if (Array.from(connectedBANodes).length) {
      filteredCanvas.nodes = filteredCanvas.nodes.map((node) => {
        if (node.type !== NodesTypes.CustomNode) return node;

        const isContextualOrConceptual = [
          ContextualSubLayerTypes.StrategicEnablers,
          ContextualSubLayerTypes.StrategicPriorities,
          ConceptualSubLayerTypes.BusinessAttributes,
          ConceptualSubLayerTypes.SecurityAttributes,
        ].includes(node.parentNode as ContextualSubLayerTypes);

        if (!isContextualOrConceptual) return node;

        return {
          ...node,
          hidden: !connectedBANodes.has(node.id),
          data: {
            ...node.data,
            canvas: {
              ...node.data.canvas,
              hiddenByFilters: !connectedBANodes.has(node.id),
              hiddenBA: !connectedBANodes.has(node.id),
            },
          },
        };
      });
    }

    filteredCanvas.edges = filteredCanvas.edges
      .concat(phantoms)
      .map((edge) => ({
        ...edge,
        hidden: filteredCanvas.nodes.some(
          ({ id, hidden }) =>
            hidden && (id === edge.source || id === edge.target),
        ),
      }));

    return filteredCanvas;
  }, [
    nodes,
    edges,
    domains,
    reviewStatus,
    validationStatus,
    phantoms,
    insightsTab,
    ciaFilters,
    questionnaireResult,
    operatingModelStatuses,
    operationalStatuses,
    partners,
  ]);
};

export default useFilteredCanvas;
